import { z } from 'zod';
import { KB_CATEGORIES, LIMITS, NOTE_STATUS } from './constants.ts';

// The Library's shapes (plan 2E Assumptions 1 and 5).

export const RecordId = z.string().regex(/^[a-z0-9]{15}$/);

type NoteValue = (typeof NOTE_STATUS)[number]['value'];

const Text = (max: number) => z.string().trim().max(max).default('');

export const KbCategory = z.enum(KB_CATEGORIES);
export type KbCategory = z.infer<typeof KbCategory>;

export const NoteStatus = z.enum(NOTE_STATUS.map((status) => status.value) as [NoteValue, ...NoteValue[]]);
export type NoteStatus = z.infer<typeof NoteStatus>;

/** Empty for no status. */
export const EntryStatus = z.union([NoteStatus, z.literal('')]);
export type EntryStatus = z.infer<typeof EntryStatus>;

export const Entry = z.object({
  id: z.string(),
  title: z.string(),
  category: KbCategory,
  status: EntryStatus,
  patch: z.string(),
  body: z.string(),
  version: z.number().int(),
  updated: z.iso.datetime(),
});
export type Entry = z.infer<typeof Entry>;

const EntryFields = z.object({
  title: z.string().trim().min(1, 'Give it a title').max(LIMITS.title),
  category: KbCategory,
  status: EntryStatus.default(''),
  patch: Text(LIMITS.patch),
  body: z.string().max(LIMITS.entryBody).default(''),
});

export const EntryInput = EntryFields;
export type EntryInput = z.input<typeof EntryInput>;

export const EntryUpdate = EntryFields.extend({ version: z.number().int().min(1) });
export type EntryUpdate = z.input<typeof EntryUpdate>;

/** A saved report on a list: no body, as it can be 40,000 characters. */
export const ReportSummary = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string(),
  preview: z.string(),
  op: z.string().nullable(),
  version: z.number().int(),
  updated: z.iso.datetime(),
});
export type ReportSummary = z.infer<typeof ReportSummary>;

export const Report = ReportSummary.omit({ preview: true }).extend({ body: z.string() });
export type Report = z.infer<typeof Report>;

const ReportFields = z.object({
  type: z.string().trim().min(1).max(LIMITS.reportType),
  title: z.string().trim().min(1, 'Give it a title').max(LIMITS.title),
  body: z.string().max(LIMITS.reportBody).default(''),
});

export const ReportInput = ReportFields.extend({ op: RecordId.nullable().default(null) });
export type ReportInput = z.input<typeof ReportInput>;

export const ReportUpdate = ReportFields.extend({ version: z.number().int().min(1) });
export type ReportUpdate = z.input<typeof ReportUpdate>;

/** Everything `GET /library/org` shows anyone logged in: no secret may go here. */
export const OrgInfo = z.object({
  cardFooter: z.string(),
  countdownName: z.string(),
  countdownDate: z.string(),
  welcomeContacts: z.string(),
  welcomeDropoffs: z.string(),
  welcomeNote: z.string(),
});
export type OrgInfo = z.infer<typeof OrgInfo>;
